import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import LightbulbRounded from "@mui/icons-material/LightbulbRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import { useCallback, useEffect, useState } from "react";
import StatusBadge from "./StatusBadge";

function metricsEntries(metrics) {
  if (!metrics || typeof metrics !== "object") return [];
  return Object.entries(metrics)
    .map(([key, meta]) => ({
      key,
      label: (meta && meta.label) || key,
      value: meta?.value,
      unit: (meta && meta.unit) || "",
      raw: meta?.raw
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

function formatMetricValue(m) {
  if (m.value === undefined || m.value === null) return m.raw != null ? String(m.raw) : "—";
  if (typeof m.value === "number" && !Number.isNaN(m.value)) return String(m.value);
  return String(m.value);
}

function ReportMetricsModal({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!row) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [row]);

  const onBackdrop = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!row) return null;

  const rows = metricsEntries(row.metrics);
  const hasMetrics = rows.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="presentation"
      onClick={onBackdrop}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-metrics-title"
        className="relative z-10 flex max-h-[min(85vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_48px_rgba(15,23,42,0.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 id="report-metrics-title" className="text-base font-semibold text-slate-900">
              Lab results
            </h2>
            <p className="mt-1 truncate text-sm text-slate-600">{row.testName}</p>
            <p className="mt-0.5 text-xs text-slate-500">
              {row.date}
              {row.fileName && row.fileName !== "—" ? ` · ${row.fileName}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close"
          >
            <CloseRounded sx={{ fontSize: 22 }} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!hasMetrics ? (
            <p className="text-sm text-slate-500">
              No numeric lab values were extracted for this report. If the PDF was scanned, try a text-based export from
              your lab.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((m) => (
                <li key={m.key} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 first:pt-0">
                  <span className="text-sm font-medium text-slate-800">{m.label}</span>
                  <span className="text-sm tabular-nums text-slate-600">
                    {formatMetricValue(m)}
                    {m.unit ? <span className="text-slate-500"> {m.unit}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
          <p className="text-xs text-slate-500">
            For reference only — confirm any important results with your clinician.
          </p>
        </div>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90">
      <div className="grid grid-cols-7 gap-0 border-b border-slate-100 bg-slate-50/90 px-4 py-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-4 animate-pulse rounded bg-slate-200/80" />
        ))}
      </div>
      {Array.from({ length: 6 }).map((_, row) => (
        <div key={row} className="grid grid-cols-7 gap-3 border-b border-slate-50 px-4 py-4">
          {Array.from({ length: 7 }).map((_, col) => (
            <div key={col} className="h-4 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80">
        <DescriptionRounded sx={{ fontSize: 36 }} className="text-slate-400" />
      </div>
      <h3 className="mt-4 text-sm font-semibold text-slate-900">No reports yet</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">
        Upload a lab PDF to populate your timeline. We’ll extract metrics and keep everything tied to your account.
      </p>
    </div>
  );
}

export default function ReportsTable({ reports, loading, onDownloadRow }) {
  const count = reports?.length ?? 0;
  const [viewRow, setViewRow] = useState(null);

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)] sm:p-5 md:p-6">
      <header className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:gap-4 sm:pb-5 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold tracking-tight text-slate-900 sm:text-lg">Medical test reports</h2>
          <p className="mt-1 text-xs text-slate-500 sm:text-sm">Complete history of your lab tests and diagnostics</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <p className="text-xs text-slate-500">
            {loading ? "Loading…" : `Showing ${count} report${count === 1 ? "" : "s"} — newest first`}
          </p>
          <div className="flex flex-wrap gap-2">
            {["Normal", "Attention", "Critical", "Pending"].map((s) => (
              <StatusBadge key={s} status={s} />
            ))}
          </div>
        </div>
      </header>

      <div className="mt-5">
        {loading ? (
          <TableSkeleton />
        ) : count === 0 ? (
          <EmptyState />
        ) : (
          <div
            className="overflow-x-auto overscroll-x-contain rounded-xl border border-slate-200/90 shadow-sm"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <table className="min-w-[920px] w-full border-collapse text-left text-xs sm:text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm">
                <tr className="border-b border-slate-200/90 text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
                  <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">Date</th>
                  <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">Test</th>
                  <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">Category</th>
                  <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">Key result</th>
                  <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">Status</th>
                  <th className="whitespace-nowrap px-3 py-2.5 sm:px-4 sm:py-3">Clinician</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right sm:px-4 sm:py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    className="transition-colors duration-200 hover:bg-slate-50/90"
                  >
                    <td className="whitespace-nowrap px-3 py-3 text-slate-600 tabular-nums sm:px-4 sm:py-3.5">{r.date}</td>
                    <td className="px-3 py-3 font-medium text-slate-900 sm:px-4 sm:py-3.5">{r.testName}</td>
                    <td className="px-3 py-3 text-slate-600 sm:px-4 sm:py-3.5">{r.category}</td>
                    <td className="max-w-[220px] truncate px-3 py-3 text-slate-600 sm:px-4 sm:py-3.5" title={r.keyResult}>
                      {r.keyResult}
                    </td>
                    <td className="px-3 py-3 sm:px-4 sm:py-3.5">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-3 py-3 text-slate-600 sm:px-4 sm:py-3.5">{r.doctor}</td>
                    <td className="px-3 py-3 text-right sm:px-4 sm:py-3.5">
                      <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setViewRow(r)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 transition-all hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 active:scale-[0.98]"
                          aria-label={`View lab results for ${r.testName}`}
                          title="View results"
                        >
                          <VisibilityRounded sx={{ fontSize: 18 }} />
                        </button>
                        <button
                          type="button"
                          onClick={() => onDownloadRow?.(r)}
                          className="inline-flex h-10 min-w-[2.5rem] items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition-all hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 active:scale-[0.98] sm:px-3.5"
                        >
                          <DownloadRounded sx={{ fontSize: 16 }} />
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && count > 0 ? (
        <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
          <LightbulbRounded sx={{ fontSize: 18 }} className="mt-0.5 shrink-0 text-amber-500" />
          Tip: Use downloads for your personal archive — always verify critical values with your care team.
        </p>
      ) : null}

      {viewRow ? <ReportMetricsModal row={viewRow} onClose={() => setViewRow(null)} /> : null}
    </div>
  );
}
